import { useQuery } from '@tanstack/react-query';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { Target, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

export const MissionsWidget = () => {
  const { data: missionsData, isLoading } = useQuery({
    queryKey: ['missions-widget'],
    queryFn: async () => {
      // First, mark any expired missions
      await supabase.rpc('mark_expired_missions');

      const { data: missions } = await supabase
        .from('missions')
        .select(`
          id, 
          status, 
          category,
          title,
          start_date,
          end_date,
          created_at,
          team_id,
          is_expired,
          teams(name)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      // Filter out expired missions from active counts
      const activeMissions = missions?.filter(m => !m.is_expired && m.status !== 'expired') || [];
      
      const pending = activeMissions.filter(m => m.status === 'pending').length || 0;
      const inProgress = activeMissions.filter(m => m.status === 'in_progress').length || 0;
      const completed = missions?.filter(m => m.status === 'completed').length || 0;
      const expired = missions?.filter(m => m.is_expired || m.status === 'expired').length || 0;

      // Get missions starting today
      const today = new Date().toISOString().split('T')[0];
      const todayMissions = missions?.filter(m => 
        m.start_date && m.start_date.startsWith(today)
      ).length || 0;

      // Get overdue missions (from active missions only)
      const now = new Date();
      const overdue = activeMissions.filter(m => 
        m.end_date && 
        fromLocalISOString(m.end_date) < now && 
        m.status !== 'completed'
      ).length || 0;

      return {
        total: activeMissions.length || 0,
        pending,
        inProgress,
        completed,
        expired,
        today: todayMissions,
        overdue,
        recentMissions: activeMissions.slice(0, 5) || []
      };
    },
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-32 bg-muted rounded"></div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <Target className="w-6 h-6 text-indigo-500" />
        <h3 className="text-lg font-semibold">Missions</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{missionsData?.inProgress || 0}</div>
          <div className="text-sm text-muted-foreground">In Progress</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{missionsData?.completed || 0}</div>
          <div className="text-sm text-muted-foreground">Completed</div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="text-sm">Pending</span>
          </div>
          <Badge variant="outline">{missionsData?.pending || 0}</Badge>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-blue-500" />
            <span className="text-sm">Today</span>
          </div>
          <Badge variant="default">{missionsData?.today || 0}</Badge>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm">Expired</span>
          </div>
          <Badge variant={missionsData?.expired ? "destructive" : "secondary"}>
            {missionsData?.expired || 0}
          </Badge>
        </div>
      </div>

      {missionsData?.recentMissions && missionsData.recentMissions.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="text-sm font-medium mb-2">Recent Missions</h4>
          <div className="space-y-1 max-h-20 overflow-y-auto">
            {missionsData.recentMissions.slice(0, 3).map((mission) => (
              <div key={mission.id} className="text-xs p-2 bg-muted rounded flex justify-between">
                <span className="truncate">{mission.title}</span>
                <Badge variant={
                  mission.status === 'pending' ? 'outline' :
                  mission.status === 'in_progress' ? 'default' :
                  mission.status === 'completed' ? 'default' : 'destructive'
                }>
                  {mission.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
